import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useActions } from '../helpers/hooks';

import {
  useCustomTranslation,
  useRouter,
  useStoreSelector,
} from '../helpers/hooks';
import {
  validateAddOns,
  getPaymentParams,
  getPaymentInvoiceResult,
  matchRoute,
} from '../helpers/business';
import { setPaymentIfNeeded } from '../actions/payment';
import verifyPayment from '../actions/verification';
import { fetchPayWay, selectPaymentPayWay } from '../actions/payWays';
import { createInvoice, initInvoiceResult } from '../actions/invoice';

import { ErrorBlock } from './Block';
import Loader from './Loader';

const ErrCode = {
  PAYWAY_ALIAS_NOT_FOUND: 300,
  PAYWAY_NOT_AVAILABLE_SHOP: 302,
  SHOP_NOT_FOUND: 500,
  SHOP_NOT_ACTIVE: 501,
  ACCOUNT_SHOP_NOT_ACTIVE: 403,
  UNABLE_CREATE_INVOICE_FOR_AGGREGATOR: 503,
  CURRENCY_NOT_AVAILABLE: 502,
  INVALID_SHOP_SIGN: 505,
  SHOP_ORDER_ID_IS_NOT_UNIQUE: 400,
  PAYER_AMOUNT_IS_TOO_SMALL: 102,
  PAYER_AMOUNT_IS_TOO_LARGE: 103,
};

export default function PaymentInitialization({ children }) {
  const { t } = useTranslation();

  const actions = useActions(
    {
      setPayment: setPaymentIfNeeded,
      verifyPayment,
      fetchPayWay,
      selectPayWay: selectPaymentPayWay,
      createInvoice,
      initInvoiceResult,
    },
    []
  );

  const { orderUnique } = useStoreSelector(
    (selector) => selector.getShop(),
    []
  );

  const [
    { initializing, initializationError },
    setInitializationState,
  ] = useState({
    initializing: true,
    initializationError: undefined,
  });

  function endInitialization() {
    setInitializationState({
      initializing: false,
      initializationError: undefined,
    });
  }

  const {
    navigate,
    location: { pathname },
  } = useRouter();
  const { urlFor } = useCustomTranslation();

  async function _init() {
    const invoiceResult = getPaymentInvoiceResult();

    if (invoiceResult) {
      actions.initInvoiceResult(invoiceResult);
      navigate(urlFor('invoiceResult'), {replace: true});
      endInitialization();
      return;
    }

    const payment = getPaymentParams();

    let payWay, addOns;

    try {
      let { payWay: p, addOns: a } = actions.setPayment(payment);

      addOns = a;
      payWay = p || payment.payway;
    } catch (e) {
      setInitializationState({
        initializing: false,
        initializationError: e,
      });
      return;
    }

    try {
      await actions.verifyPayment();
    } catch (e) {
      if (e != null && e.type != null) {
        switch (e.type) {
          case 'alreadyPaid': {
            navigate(urlFor('invoiceResult'), {replace: true});
            endInitialization();
            return;
          }
          case 'alreadyCreated': {
            navigate(urlFor('payment'), {replace: true});
            endInitialization();
            return;
          }
          case 'alreadyVerified': {
            break;
          }
          default: {
            setInitializationState({
              initializing: false,
              initializationError: e,
            });
            return;
          }
        }
      }

      if (e != null && e.data != null && e.data.errCode != null) {
        switch (e.data.errCode) {
          case ErrCode.SHOP_NOT_FOUND:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.ShopNotFound', {
                      shopId: e.data.message.match(/(\d+)/gm)[0] || null,
                    }),
                },
              },
            });
            return;

          case ErrCode.SHOP_NOT_ACTIVE:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.ShopNotActive', {
                      shopId: e.data.message.match(/(\d+)/gm)[0] || null,
                    }),
                },
              },
            });
            return;

          case ErrCode.ACCOUNT_SHOP_NOT_ACTIVE:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.AccountShopNotActive', {
                      shopId: e.data.message.match(/(\d+)/gm)[0] || null,
                    }),
                },
              },
            });
            return;

          case ErrCode.UNABLE_CREATE_INVOICE_FOR_AGGREGATOR:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.UnableCreateInvoiceForAggregator', {
                      shopId: e.data.message.match(/(\d+)/gm)[0] || null,
                    }),
                },
              },
            });
            return;

          case ErrCode.CURRENCY_NOT_AVAILABLE:
            const [, currencyId, shopId] = e.data.message.match(
              /^Currency\s\(([0-9]+)\)\sis not available for shop\(id=([0-9]+)\)/
            );

            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.CurrencyNotAvailable', {
                      currencyId: currencyId ? currencyId : null,
                      shopId: shopId ? shopId : null,
                    }),
                },
              },
            });
            return;

          case ErrCode.INVALID_SHOP_SIGN:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.InvalidShopSign', {
                      value: e.data.message
                        ? `"${e.data.message.replace(
                            /^Invalid shop sign, string_to_sign:\s/i,
                            ''
                          )}"`
                        : null,
                    }),
                },
              },
            });
            return;
          case ErrCode.SHOP_ORDER_ID_IS_NOT_UNIQUE:
            setInitializationState({
              initializing: false,
              initializationError: {
                data: {
                  message: (t) =>
                    t('Verification.ShopOrderIdIsNotUnique', {
                      shopOrderId: e.data.message
                        ? e.data.message
                            .replace(/^shop_order_id '/i, '')
                            .replace(/' isn`t unique$/i, '')
                        : null,
                    }),
                },
              },
            });
            return;
          default:
            setInitializationState({
              initializing: false,
              initializationError: new Error('unexpected error'),
            });
            return;
        }
      }

      setInitializationState({
        initializing: false,
        initializationError: new Error('unexpected error'),
      });
      return;
    }

    if (payWay != null && payWay !== '') {
      let payWayResp;

      try {
        payWayResp = await actions.fetchPayWay(payWay);
      } catch (e) {
        if (e != null && e.data != null && e.data.errCode != null) {
          switch (e.data.errCode) {
            case ErrCode.PAYWAY_ALIAS_NOT_FOUND:
              setInitializationState({
                initializing: false,
                initializationError: {
                  data: {
                    message: (t) =>
                      t('Verification.PaywayAliasNotFound', {
                        alias: e.data.message.match(/.*'(.*)'.*/)[1] || null,
                      }),
                  },
                },
              });
              return;

            case ErrCode.PAYWAY_NOT_AVAILABLE_SHOP:
              setInitializationState({
                initializing: false,
                initializationError: {
                  data: {
                    message: (t) => t('Verification.PaywayNotAvailableShop'),
                  },
                },
              });
              return;

            case ErrCode.PAYER_AMOUNT_IS_TOO_SMALL:
              setInitializationState({
                initializing: false,
                initializationError: {
                  data: {
                    message: (t) =>
                      t(`PayWays.Errors.${ErrCode.PAYER_AMOUNT_IS_TOO_SMALL}`, {
                        amount: e.data.message.match(/min: (.*)$/)[1] || null,
                      }),
                  },
                },
              });
              return;

            case ErrCode.PAYER_AMOUNT_IS_TOO_LARGE:
              setInitializationState({
                initializing: false,
                initializationError: {
                  data: {
                    message: (t) =>
                      t(`PayWays.Errors.${ErrCode.PAYER_AMOUNT_IS_TOO_LARGE}`, {
                        amount: e.data.message.match(/max: (.*)$/)[1] || null,
                      }),
                  },
                },
              });
              return;

            default:
              endInitialization();
              return;
          }
        }
      }

      const {
        details: { addOns: addOnsTemplate },
        pmId,
      } = payWayResp;

      if (!validateAddOns(addOnsTemplate, addOns)) {
        actions.selectPayWay(payWay);
        navigate(urlFor('payWaySelector', { payMethodId: pmId }), {replace: true});
        endInitialization();
      } else {
        try {
          await actions.createInvoice(payWay, addOns);
        } catch (e) {
          actions.selectPayWay(payWay);
          navigate(urlFor('payWaySelector', { payMethodId: pmId }), {replace: true});
          endInitialization();
          return;
        }

        navigate(urlFor('payment'), {replace: true});
        endInitialization();
      }
    } else {
      if (!orderUnique && matchRoute(pathname) === 'payment') {
        navigate(urlFor('payMethodSelector'), {replace: true});
      }
      endInitialization();
    }
  }

  useEffect(() => {
    _init();
  }, []);

  if (initializationError) {
    return (
      <ErrorBlock headerText={t('Error')} text={t('Verification.Error')}>
        {initializationError.data && initializationError.data.message && (
          <p>
            {typeof initializationError.data.message === 'function'
              ? initializationError.data.message(t)
              : initializationError.data.message}
          </p>
        )}
      </ErrorBlock>
    );
  }

  if (initializing) {
    return <Loader />;
  }

  return children;
}
